import type { DividerCdtLevel } from "@CommonTypes/backend/PspRules";
import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { getActiveShelfDividers } from "@CommonUtils/pog/shelfDividers";
import {
  DIVIDER_CDT_LEVEL_LABELS,
  dividerAttributeLabel,
  shelfDividerWhy,
  summarizeShelfDividerNeeds,
  type ShelfDividerReason,
  type ShelfDividerWhy,
} from "@CommonUtils/pog/dividerPolicy";
import { translate } from "src/i18n/translate";
import { formatDimension } from "src/utils/formatDimension";

export interface ShelfDividerRow {
  label: string;
  value: string;
}

const describeEnds = (atStart: boolean, atEnd: boolean) =>
  translate(
    "common",
    atStart && atEnd
      ? "shelfDividers.ends.both"
      : atStart
        ? "shelfDividers.ends.start"
        : atEnd
          ? "shelfDividers.ends.end"
          : "shelfDividers.ends.neither",
  );

const isCdtLevel = (field: string): field is DividerCdtLevel => field in DIVIDER_CDT_LEVEL_LABELS;

const formatDividerReason = (reason: ShelfDividerReason): string => {
  if (reason.code === "stored") return reason.text;
  const label =
    reason.kind === "cdt" && isCdtLevel(reason.field)
      ? DIVIDER_CDT_LEVEL_LABELS[reason.field]
      : dividerAttributeLabel(reason.field);
  return reason.value
    ? translate("common", "shelfDividers.why.valueIs", { label, value: reason.value })
    : translate("common", "shelfDividers.why.noValue", { label });
};

/** `shelfDividerWhy` in words. */
export const formatShelfDividerWhy = (why: ShelfDividerWhy): string => {
  switch (why.code) {
    case "set-always":
      return translate("common", "shelfDividers.why.setAlways");
    case "kept-from-fixture":
      return translate("common", "shelfDividers.why.keptFromFixture");
    case "set-never":
      return why.needing === undefined || why.facings === undefined
        ? translate("common", "shelfDividers.why.setNever")
        : translate("common", "shelfDividers.why.setNeverWithCounts", {
            needing: why.needing,
            facings: why.facings,
          });
    case "not-needed":
      return translate("common", "shelfDividers.why.notNeeded");
    case "needed": {
      const counts = { needing: why.needing, facings: why.facings };
      const reasons = why.reasons.map(formatDividerReason);
      const notes = why.needsMoreThanHalf
        ? [...reasons, translate("common", "shelfDividers.why.needsMoreThanHalf")]
        : reasons;
      return notes.length > 0
        ? translate("common", "shelfDividers.why.neededWithNotes", {
            ...counts,
            notes: notes.join("; "),
          })
        : translate("common", "shelfDividers.why.needed", counts);
    }
  }
};

/** Why the shelf has dividers or not under the flow's policy; empty for pogs generated without one. */
export const shelfDividerReason = (shelf: PlanogramShelf): string => {
  const why = shelfDividerWhy({
    summary: summarizeShelfDividerNeeds(
      shelf
        .getMergedShelves()
        .flatMap((member) => member.items)
        .map((item) => ({ columns: item.getHorizontalFacings(), dividerNeed: item.dividerNeed })),
    ),
    dividers: shelf.dividers,
    setByHand: shelf.dividersSetByHand,
    keptFromFixture: shelf.dividersKeptFromFixture,
  });
  return why ? formatShelfDividerWhy(why) : "";
};

const withReason = (shelf: PlanogramShelf, rows: ShelfDividerRow[]): ShelfDividerRow[] => {
  const reason = shelfDividerReason(shelf);
  return reason
    ? [...rows, { label: translate("common", "shelfDividers.rows.why"), value: reason }]
    : rows;
};

/**
 * The shelf's divider hardware as panel rows. A shelf without active dividers
 * (none, switched off, or a JDA default record with width 0) shows one
 * "Dividers: None" row; width, ends and space only show when dividers apply.
 */
export const shelfDividerRows = (shelf: PlanogramShelf): ShelfDividerRow[] => {
  const { dividers } = shelf;
  const active = getActiveShelfDividers(dividers);
  const dividersLabel = translate("common", "shelfDividers.rows.dividers");
  if (!dividers || !active) {
    return withReason(shelf, [
      { label: dividersLabel, value: translate("common", "shelfDividers.rows.none") },
    ]);
  }

  const mode =
    dividers.numberOfDividers === "dividers_between_facings"
      ? translate("common", "shelfDividers.rows.betweenEveryFacing")
      : translate("common", "shelfDividers.rows.count", { count: dividers.numberOfDividers });
  const productWidth = shelf
    .getMergedShelves()
    .flatMap((member) => member.items)
    .reduce((total, item) => total + item.getBlockWidth() + item.getXCappingWidth(), 0);
  return withReason(shelf, [
    { label: dividersLabel, value: mode },
    {
      label: translate("common", "shelfDividers.rows.width"),
      value: `${formatDimension(dividers.width)}`,
    },
    {
      label: translate("common", "shelfDividers.rows.ends"),
      value: describeEnds(dividers.atStart, dividers.atEnd),
    },
    {
      label: translate("common", "shelfDividers.rows.space"),
      value: `${formatDimension(Math.round(shelf.getItemsWidth() - productWidth))}`,
    },
  ]);
};
